import { Section } from '../components/ui/Section'
import { Button } from '../components/ui/Button'

export function AboutPage() {
  return (
    <>
      {/* Hero */}
      <section className="bg-gradient-to-b from-slate-50 to-white py-20 md:py-28">
        <div className="container-narrow">
          <h1 className="text-4xl md:text-5xl font-bold text-slate-900">
            About Sum1 Solutions
          </h1>
          <p className="mt-8 text-xl text-slate-600 leading-relaxed">
            A small company building useful technology, testing better ways for
            people and agents to work together, and keeping human needs and
            accountability visible throughout the process.
          </p>
        </div>
      </section>

      {/* Main Content */}
      <Section narrow className="bg-white pt-0">
        <div className="space-y-12 text-lg text-slate-600 leading-relaxed">

          {/* The Founder */}
          <div>
            <h2 className="text-2xl font-semibold text-slate-900 mb-6">The Founder</h2>
            <p className="mb-6">
              Sum1 Solutions was founded by a family physician in Rhode Island who also happens to build software.
            </p>
            <p className="mb-6">
              In 2001, frustrated that no affordable EHR existed for small
              practices, he wrote his own. That became{' '}
              <a
                href="https://amazingcharts.com/"
                target="_blank"
                rel="noreferrer"
                className="text-primary-600 hover:text-primary-700 underline"
              >
                Amazing Charts
              </a>
              , which grew to serve over 6,000 medical practices before being
              acquired in 2012. He was named Physician Entrepreneur of the Year
              by Modern Physician in 2010.
            </p>
            <p className="mb-6">
              But the real motivation was never software. It was reducing the friction between a person who needs help and the help they need. EHRs were supposed to do that. Mostly they didn't. So now he's trying other approaches.
            </p>
          </div>

          {/* Why This Work */}
          <div>
            <h2 className="text-2xl font-semibold text-slate-900 mb-6">Why This Work</h2>
            <p className="mb-6">
              Some of the work here—including support tools and research into
              difficult human experiences—comes from clinical and personal
              experience.
            </p>
            <p className="mb-6">
              Panic attacks. Medication side effects. Akathisia (that unbearable restlessness that some psychiatric medications cause). The kind of darkness that makes you understand why people don't make it through.
            </p>
            <p className="mb-6">
              The tools exist because they might have helped in moments when
              useful support was hard to reach. Research and language can
              sometimes make an experience easier to understand, but that
              possibility must be tested rather than promised.
            </p>
            <p className="text-slate-500 italic">
              None of this is a replacement for professional help. It's what you might reach for at 3am when no one's coming.
            </p>
          </div>

          {/* What We Believe */}
          <div>
            <h2 className="text-2xl font-semibold text-slate-900 mb-6">What We Believe</h2>
            <ul className="space-y-4">
              <li className="flex gap-4">
                <span className="text-primary-600 font-bold">→</span>
                <span><strong>Start with the person and the actual job.</strong> Technology should answer a concrete need rather than force people into the shape of a system.</span>
              </li>
              <li className="flex gap-4">
                <span className="text-primary-600 font-bold">→</span>
                <span><strong>Tools should meet you where you are.</strong> Not where you should be. Not where you wish you were. Where you actually are, right now.</span>
              </li>
              <li className="flex gap-4">
                <span className="text-primary-600 font-bold">→</span>
                <span><strong>Human accountability stays in the loop.</strong> Agents may research, draft, and act within explicit boundaries; people remain responsible for consequential decisions.</span>
              </li>
              <li className="flex gap-4">
                <span className="text-primary-600 font-bold">→</span>
                <span><strong>Theory earns its place through application and evidence.</strong> A compelling framework is not proof that a product works or that people benefit.</span>
              </li>
              <li className="flex gap-4">
                <span className="text-primary-600 font-bold">→</span>
                <span><strong>We might be wrong.</strong> About a lot of this. That's built in. Certainty is part of the problem.</span>
              </li>
            </ul>
          </div>

          {/* The Name */}
          <div>
            <h2 className="text-2xl font-semibold text-slate-900 mb-6">The Name</h2>
            <p className="mb-6">
              "Sum1" is a pun. It sounds like "someone" — as in "someone who cares." But it's also a math reference: Σ1, or "sum of one."
            </p>
            <p className="mb-6">
              It does not mean that every person is a separate unit sealed from
              everyone else. The recognition is closer to the reverse: we
              experience many of the same fears, feelings, needs, and moments
              of connection. We are distinct perspectives within one shared
              existence, divided in lived experience by what has happened to
              us and by how we perceive and carry it.
            </p>
            <p className="mb-6">
              <strong>Sum1 Solutions</strong> also means the solutions that we
              can find together but may never find alone. A choir does not
              become powerful by reducing every voice to the same note. Each
              voice remains distinct, listens, adjusts, and contributes what
              the others cannot. The resulting harmony belongs to the whole
              while depending on every participant.
            </p>
            <p className="mb-6">
              That recognition became the starting intuition for
              <strong> E1</strong> and <strong>Existential Math</strong>. E1
              begins with <strong>∃ 1</strong>: at minimum, there is this—some
              existence, occurrence, or appearance. The wider framework asks
              what follows from that ground, what must be added, and how
              distinction, perspective, identity, and relationship arise.
            </p>
            <p className="text-slate-500">
              The movement from “something exists” to “we are perspectives
              within one existence” is Sum1's philosophical interpretation,
              not a settled scientific result or a theorem forced by ∃ 1.
              Existential Math keeps that distinction visible. No one needs to
              accept the philosophy to use or evaluate a Sum1 product; products
              must stand on their own evidence.
            </p>
          </div>

        </div>

        {/* CTA */}
        <div className="mt-16 p-10 bg-slate-50 rounded-xl">
          <div className="text-center">
            <h3 className="text-xl font-semibold text-slate-900 mb-4">
              Want to learn more or collaborate?
            </h3>
            <p className="text-slate-600 mb-8 leading-relaxed">
              Whether you're a researcher, clinician, developer, operator, or
              someone with a concrete problem and useful experience—we'd like
              to hear from you.
            </p>
            <Button href="/contact" size="lg">
              Get in Touch
            </Button>
          </div>
        </div>
      </Section>
    </>
  )
}
